import axios from 'axios'; // Use configured axios instance (auto-adds auth + ip headers)
import BASEURL from '../Config/global';

/**
 * Core helper: call backend proxy for Google Translate
 *
 * Backend route: POST `${BASEURL}/api/misc/translate/`
 * Request body: { text: string | string[], target: string, source?: string }
 * Response: { translated_text: string[] }
 *
 * Uses configured axios instance which automatically adds:
 * - Authorization: Token (from localStorage)
 * - ip: header (from localStorage)
 * - Accept-Language: header (from localStorage)
 */
const translateViaBackend = async ({ text, target, source = null }) => {
    if (!text || text.length === 0) {
        return {
            success: false,
            error: 'No text provided for translation',
            originalText: text,
        };
    }
    // const value = [text];
    try {
        const payload = {
            text:[text],
            target,
            // Only include source if provided (allow backend to auto-detect if null)
            ...(source ? { source } : {}),
        };

        const response = await axios.post(`${BASEURL}/api/misc/translate/`, payload);

        // Backend returns: { translated_text: string[] }
        if (response.data && Array.isArray(response.data.translated_text)) {
            const translatedTexts = response.data.translated_text;

            // Normalize to array of objects for consistent handling
            const normalizedTranslations = translatedTexts.map((translatedText) => ({
                translatedText,
            }));

            return {
                success: true,
                translations: normalizedTranslations,
                originalText: text,
            };
        }

        return {
            success: false,
            error: 'Invalid response format from translation backend',
            originalText: text,
        };
    } catch (error) {
        console.error('Translation Backend Error:', error);
        return {
            success: false,
            error:
                error.response?.data?.error?.message ||
                error.response?.data?.error ||
                error.message ||
                'Translation failed',
            originalText: text,
        };
    }
};

/**
 * Translate text to Spanish (single)
 */
export const translateToSpanish = async (text, sourceLanguage = null) => {
    const result = await translateViaBackend({
        text,
        target: 'es',
        source: sourceLanguage,
    });

    if (!result.success || !result.translations?.length) return result;

    return {
        success: true,
        translatedText: result.translations[0].translatedText,
        detectedSourceLanguage: result.translations[0].detectedSourceLanguage,
        originalText: text,
    };
};

/**
 * Translate text from Spanish to English (single)
 */
export const translateFromSpanish = async (text) => {
    const result = await translateViaBackend({
        text,
        target: 'en',
        source: 'es',
    });

    if (!result.success || !result.translations?.length) return result;

    return {
        success: true,
        translatedText: result.translations[0].translatedText,
        detectedSourceLanguage: 'es',
        originalText: text,
    };
};

/**
 * Translate English to Spanish (single)
 */
export const translateEnglishToSpanish = async (text) => {
    return await translateToSpanish(text, 'en');
};

/**
 * Batch translate English -> Spanish
 * - text array goes to backend as `text: string[]`
 * - backend should pass it to Google as multiple `q` values
 */
export const translateEnglishToSpanishBatch = async (texts = []) => {
    if (!Array.isArray(texts) || texts.length === 0) {
        return { success: false, error: 'No texts provided for translation' };
    }

    const result = await translateViaBackend({
        text: texts,
        target: 'es',
        source: 'en',
    });

    if (!result.success || !Array.isArray(result.translations)) {
        return {
            success: false,
            error: result.error || 'Batch translation failed',
        };
    }

    const translatedTexts = result.translations.map((t) => t.translatedText);

    return {
        success: true,
        translatedTexts,
    };
};
